<template>
  <div>
    <div class="mb-32">
      <strong>{{ $t('insuranceCover.extras.title') }}</strong>
      <br />
      <p v-dompurify-html="$t('insuranceCover.extras.hint')"></p>
    </div>

    <div class="leistungsextras-container">
      <div
        v-for="extra in Object.keys(selectedExtras.liabilityProduct.extras)
          .sort()
          .values()"
        class="extra"
        :key="extra"
      >
        <ChangeLeistungsextraCheckbox
          @input="emitExtras"
          :label="extra"
          :value="
            selectedExtras.liabilityProduct.extras[extra as keyof HaftpflichtExtras] ??
            false
          "
          kaskodeckung="haftpflicht"
        ></ChangeLeistungsextraCheckbox>
      </div>
      <div
        v-for="extra in Object.keys(
          selectedExtras.comprehensiveInsuranceProduct?.extras ?? {}
        )
          .sort()
          .values()"
        class="extra"
        :key="extra"
      >
        <ChangeLeistungsextraCheckbox
          @input="emitExtras"
          :label="extra"
          :value="
            selectedExtras.comprehensiveInsuranceProduct?.extras[
              extra as keyof KaskoExtras
            ] ?? false
          "
          kaskodeckung="kasko"
        ></ChangeLeistungsextraCheckbox>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, type PropType, reactive, watch } from 'vue'
import {
  ComprehensiveCover,
  type Extras,
  type KaskoProduct
} from '@/typesTenant/MotorContractChange'
import ChangeLeistungsextraCheckbox from '@/componentsTenant/motor/cockpit/ChangeLeistungsextraCheckbox.vue'
import type { HaftpflichtExtras, KaskoExtras } from '@/types/policy/PolicyTypes'

const props = defineProps({
  selectedKasko: {
    type: String as PropType<ComprehensiveCover>,
    required: true
  },
  value: {
    type: Object as PropType<Extras>,
    required: true
  },
  teilkaskoProduct: {
    type: Object as PropType<KaskoProduct>,
    required: true
  },
  vollkaskoProduct: {
    type: Object as PropType<KaskoProduct>,
    required: true
  }
})

const emit = defineEmits<{
  input: [value: any]
}>()

const selectedExtras = reactive({
  liabilityProduct: {
    productKey: props.value.liabilityProduct.productKey,
    extras: { ...props.value.liabilityProduct.extras }
  },
  comprehensiveInsuranceProduct: props.value.comprehensiveInsuranceProduct
    ? {
        productKey: props.value.comprehensiveInsuranceProduct.productKey,
        extras: { ...props.value.comprehensiveInsuranceProduct.extras }
      }
    : { productKey: '', extras: {} }
})

onMounted(() => {
  currentSelectedExtras()
})

watch(
  () => props.selectedKasko,
  () => {
    currentSelectedExtras()
  }
)

function currentSelectedExtras() {
  if (props.selectedKasko === ComprehensiveCover.NO_COMPREHENSIVE) {
    selectedExtras.comprehensiveInsuranceProduct = { productKey: '', extras: {} }
  } else if (props.selectedKasko === ComprehensiveCover.PARTIALLY_COMPREHENSIVE) {
    selectedExtras.comprehensiveInsuranceProduct = {
      productKey: props.teilkaskoProduct.productKey,
      extras: { ...props.teilkaskoProduct.extras }
    }
  } else {
    selectedExtras.comprehensiveInsuranceProduct = {
      productKey: props.vollkaskoProduct.productKey,
      extras: { ...props.vollkaskoProduct.extras }
    }
  }
  emitExtras(true, '', '')
}

function isHaftpflichtExtrasKey(key: string): key is keyof HaftpflichtExtras {
  return key === 'FAHRERSCHUTZ' || key === 'RABATTSCHUTZ'
}

function isKaskoExtrasKey(key: string): key is keyof KaskoExtras {
  return [
    'ERSATZWAGEN',
    'ERWEITERTE_NEUWERTENTSCHAEDIGUNG',
    'FREIE_WERKSTATTWAHL',
    'GAP_DECKUNG',
    'KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN',
    'RABATTSCHUTZ',
    'TIERBISSFOLGESCHAEDEN_GEMAESS_AKB',
    'VERSICHERUNGSSCHUTZ_BEI_GROBER_FAHRLAESSIGKEIT',
    'ZUSAMMENSTOSS_MIT_ALLEN_TIEREN'
  ].includes(key)
}

function emitExtras(newValue: boolean, label: string, kaskodeckung: string): void {
  if (
    kaskodeckung === 'kasko' &&
    isKaskoExtrasKey(label) &&
    selectedExtras.comprehensiveInsuranceProduct?.extras[label] !== undefined
  ) {
    selectedExtras.comprehensiveInsuranceProduct.extras[label] = newValue
  }
  if (
    kaskodeckung === 'haftpflicht' &&
    isHaftpflichtExtrasKey(label) &&
    selectedExtras.liabilityProduct.extras[label] !== undefined
  ) {
    selectedExtras.liabilityProduct.extras[label] = newValue
  }

  // Ersatzwagen and FreieWerkstattwahl are connected
  // FreieWerkstattwahl is the precondition of selecting Ersatzwagen
  if (
    selectedExtras.comprehensiveInsuranceProduct &&
    selectedExtras.comprehensiveInsuranceProduct.extras
  ) {
    if (
      label === 'FREIE_WERKSTATTWAHL' &&
      selectedExtras.comprehensiveInsuranceProduct.extras.FREIE_WERKSTATTWAHL === false
    ) {
      selectedExtras.comprehensiveInsuranceProduct.extras.ERSATZWAGEN = false
    }
    if (
      label === 'ERSATZWAGEN' &&
      selectedExtras.comprehensiveInsuranceProduct.extras.ERSATZWAGEN === true
    ) {
      selectedExtras.comprehensiveInsuranceProduct.extras.FREIE_WERKSTATTWAHL = true
    }
  }
  emit('input', selectedExtras)
}
</script>

<style lang="scss" scoped>
.text {
  font-size: 14px;
  color: var(--primary-color-0);
  font-family: var(--f-primary-base), serif;
  padding-bottom: 16px;
}

.leistungsextras-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

@media (max-width: 799px) {
  .leistungsextras-container .extra {
    width: 100%;
  }
}
</style>
